import type { ReactNode } from 'react';

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: T) => ReactNode;
  readonly width?: string;
  readonly align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<T> {
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly total?: number;
  readonly page?: number;
  readonly pageSize?: number;
  readonly onPageChange?: (page: number) => void;
  readonly loading?: boolean;
  readonly error?: string | null;
  readonly emptyMessage?: string;
  readonly onRowClick?: (row: T) => void;
}

/**
 * Generic table component used across every dashboard listing page.
 * Sticky header, alternating-row hover state via CSS, server-driven
 * pagination (consumer manages page / pageSize state and refetches).
 * Loading + error + empty states are all handled here so pages stay
 * declarative.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  total,
  page,
  pageSize,
  onPageChange,
  loading,
  error,
  emptyMessage,
  onRowClick,
}: DataTableProps<T>) {
  const totalPages =
    total && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1;

  return (
    <div className="nw-table-wrap">
      <table className="nw-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  width: col.width,
                  textAlign: col.align ?? 'left',
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="nw-table__loading">
                Loading…
              </td>
            </tr>
          )}
          {!loading && error && (
            <tr>
              <td colSpan={columns.length} className="nw-table__error">
                {error}
              </td>
            </tr>
          )}
          {!loading && !error && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="nw-table__empty">
                {emptyMessage ?? 'No results.'}
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={onRowClick ? 'nw-table__row--clickable' : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{ textAlign: col.align ?? 'left' }}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {total !== undefined && page !== undefined && pageSize !== undefined && (
        <div className="nw-table__pager">
          <span className="nw-table__pager-count">
            Page {page} of {totalPages} · {total.toLocaleString()} rows
          </span>
          <div className="nw-table__pager-actions">
            <button
              type="button"
              className="nw-btn nw-btn--secondary"
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="nw-btn nw-btn--secondary"
              disabled={page >= totalPages}
              onClick={() => onPageChange?.(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
